import { z } from 'zod';
import { FlowHumanTaskConditionSchema } from './conditional.js';
import { FlowBlockValueFieldSchema, FlowBlockValueFieldSchemaVariant, } from '../../blockValue/fieldSchema.js';
import { FlowBlockValueStringSchema } from '../../blockValue/stringSchema.js';
import { FlowBlockValueTemplateHint, validateFlowBlockValueTemplate, } from '../../blockValue/template.js';
export var FlowHumanTaskBlockFieldType;
(function (FlowHumanTaskBlockFieldType) {
    FlowHumanTaskBlockFieldType["ContentMarkdown"] = "ContentMarkdown";
    FlowHumanTaskBlockFieldType["ReadOnlyValue"] = "ReadOnlyValue";
    FlowHumanTaskBlockFieldType["EditableValue"] = "EditableValue";
    FlowHumanTaskBlockFieldType["ImageUpload"] = "ImageUpload";
    FlowHumanTaskBlockFieldType["FileUpload"] = "FileUpload";
    FlowHumanTaskBlockFieldType["DateOnlyValue"] = "DateOnlyValue";
    FlowHumanTaskBlockFieldType["SingleOptionValue"] = "SingleOptionValue";
    FlowHumanTaskBlockFieldType["MultiOptionValue"] = "MultiOptionValue";
    FlowHumanTaskBlockFieldType["FileReferenceDownload"] = "FileReferenceDownload";
})(FlowHumanTaskBlockFieldType || (FlowHumanTaskBlockFieldType = {}));
export const FlowHumanTaskReadOnlyFields = new Set([
    FlowHumanTaskBlockFieldType.ContentMarkdown,
    FlowHumanTaskBlockFieldType.ReadOnlyValue,
    FlowHumanTaskBlockFieldType.FileReferenceDownload,
]);
const VariableNameFieldSchema = z.string().superRefine((value, context) => {
    const flowTemplateValidation = validateFlowBlockValueTemplate(value);
    if (flowTemplateValidation.hint === FlowBlockValueTemplateHint.Valid) {
        return;
    }
    if ('message' in flowTemplateValidation) {
        context.addIssue({
            code: 'custom',
            message: flowTemplateValidation.message,
        });
    }
    if (flowTemplateValidation.hint === FlowBlockValueTemplateHint.NotTemplateLike) {
        if (!/^[a-zA-Z]\w*$/.test(value)) {
            context.addIssue({
                code: 'custom',
                message: 'Must start with a letter, followed by letters, numbers, or underscores. e.g. variableName',
            });
        }
    }
});
export const BaseFlowHumanTaskFieldSchema = z.object({
    id: z.string(),
    isVisibleConditions: z.array(FlowHumanTaskConditionSchema).optional(),
});
export const BaseEditableFlowHumanTaskFieldSchema = BaseFlowHumanTaskFieldSchema.extend({
    isRequiredConditions: z.array(FlowHumanTaskConditionSchema).optional(),
});
export const FlowHumanTaskContentMarkdownFieldSchema = BaseFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.ContentMarkdown),
    content: z.string(),
});
export const FlowHumanTaskReadOnlyValueFieldSchema = BaseFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.ReadOnlyValue),
    label: z.string(),
    value: z.string(),
    helperText: z.string().optional(),
});
export const FlowHumanTaskEditableValueFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.EditableValue),
    label: z.string(),
    variableName: VariableNameFieldSchema,
    initialValue: z.string(),
    helperText: z.string().optional(),
});
export const FlowHumanTaskImageUploadFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.ImageUpload),
    label: z.string(),
    variableName: VariableNameFieldSchema,
    helperText: z.string().optional(),
});
export const FlowHumanTaskFileUploadFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.FileUpload),
    label: z.string(),
    variableName: VariableNameFieldSchema,
    helperText: z.string().optional(),
});
export var FlowHumanTaskFieldOptionsVariant;
(function (FlowHumanTaskFieldOptionsVariant) {
    FlowHumanTaskFieldOptionsVariant["Static"] = "Static";
    FlowHumanTaskFieldOptionsVariant["Dynamic"] = "Dynamic";
})(FlowHumanTaskFieldOptionsVariant || (FlowHumanTaskFieldOptionsVariant = {}));
export const FlowHumanTaskFieldOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
    value: z.string().optional(),
});
export const FlowHumanTaskDateOnlyValueSchema = z.union([
    z.literal(''),
    z.iso.date({ error: 'Must be a valid date in YYYY-MM-DD format or an empty string' }),
]);
export const FlowHumanTaskDateOnlyValueFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.DateOnlyValue),
    label: z.string(),
    variableName: VariableNameFieldSchema,
    initialValue: FlowBlockValueStringSchema({
        stringValidationSchema: FlowHumanTaskDateOnlyValueSchema,
    }),
    helperText: z.string().optional(),
});
export const FlowHumanTaskSingleOptionValueFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.SingleOptionValue),
    label: z.string(),
    optionsVariant: z
        .enum(FlowHumanTaskFieldOptionsVariant)
        .optional()
        .default(FlowHumanTaskFieldOptionsVariant.Static),
    options: z.array(FlowHumanTaskFieldOptionSchema),
    dynamicOptions: FlowBlockValueFieldSchema({
        variant: FlowBlockValueFieldSchemaVariant.TemplateAndPlainString,
    }).default(''),
    variableName: VariableNameFieldSchema,
    initialValue: z.string(),
});
export const FlowHumanTaskMultiOptionValueFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.MultiOptionValue),
    label: z.string(),
    optionsVariant: z
        .enum(FlowHumanTaskFieldOptionsVariant)
        .optional()
        .default(FlowHumanTaskFieldOptionsVariant.Static),
    options: z.array(FlowHumanTaskFieldOptionSchema),
    dynamicOptions: FlowBlockValueFieldSchema({
        variant: FlowBlockValueFieldSchemaVariant.TemplateAndPlainString,
    }).default(''),
    variableName: VariableNameFieldSchema,
    initialValue: FlowBlockValueFieldSchema({
        variant: FlowBlockValueFieldSchemaVariant.OptionalTemplate,
    }).or(
    /**
     * This is a workaround for the fact that the initialValue is a string, but converted to an array when the block value
     * template is parsed. The frontend always expects a string, but the parsed backend value is an array.
     */
    z.array(z.string())),
});
export const FlowHumanTaskFileReferenceDownloadFieldSchema = BaseEditableFlowHumanTaskFieldSchema.extend({
    fieldType: z.literal(FlowHumanTaskBlockFieldType.FileReferenceDownload),
    id: z.string(),
    label: z.string(),
    value: z.string(),
});
export const FlowHumanTaskFieldSchema = z.discriminatedUnion('fieldType', [
    FlowHumanTaskContentMarkdownFieldSchema,
    FlowHumanTaskReadOnlyValueFieldSchema,
    FlowHumanTaskEditableValueFieldSchema,
    FlowHumanTaskImageUploadFieldSchema,
    FlowHumanTaskFileUploadFieldSchema,
    FlowHumanTaskDateOnlyValueFieldSchema,
    FlowHumanTaskSingleOptionValueFieldSchema,
    FlowHumanTaskMultiOptionValueFieldSchema,
    FlowHumanTaskFileReferenceDownloadFieldSchema,
]);
export const FlowHumanTaskStepSchema = z.object({
    id: z.string(),
    title: z.string(),
    fields: z.array(FlowHumanTaskFieldSchema),
    isVisibleConditions: z.array(FlowHumanTaskConditionSchema).optional(),
});
//# sourceMappingURL=taskConfig.js.map